<template>
    <div>
       <div :class="{ 'fill-height d-flex flex-column': !insideDialog }" v-if="!isMobile">
           <v-overlay :value="isLoading">
               <v-progress-circular indeterminate size="64"></v-progress-circular>
           </v-overlay>

           <template v-if="!hasItems">
               <div class="d-flex align-center justify-center mt-16">
                   <v-icon class="appbar-icon-button btn-bg-color-grey grey-color"> mdi-cart-variant </v-icon>
               </div>

               <div class="d-flex align-center justify-center mt-4 mb-8">
                   <p class="text-h6 grey-color" data-cy="empty-shopping-cart-text">{{ $t('cart.empty') }}</p>
               </div>
           </template>

           <div v-else class="mt-2 fill-height myprovet-shopping-cart-list">
               <v-row class="no-gutters mx-2 mb-0">
                   <v-col cols="12" :style="calculateHeaderColWidth('designation')" class="product">
                       <div class="d-flex fill-height align-center justify-start">
                           <strong class="dark-blue-color">{{ $t('campaigns.product') }}</strong>
                       </div>
                   </v-col>

                   <v-col cols="12" v-if="!insideDialog" style="width: 7%; max-width: 7%" class="substitute">
                       <div class="d-flex fill-height align-center justify-center">
                           <div v-if="showSubstituteProductsColumn">
                               <strong class="dark-blue-color">{{ $t('cart.replace') }}</strong>
                           </div>
                       </div>
                   </v-col>

                   <v-col cols="12" :style="calculateHeaderColWidth('quantity')" class="quantity">
                       <div class="d-flex fill-height align-center justify-center">
                           <strong class="dark-blue-color">{{ $t('orders.base.quantity') }}</strong>
                       </div>
                   </v-col>

                   <v-col v-if="canViewBonusHeader" cols="12" style="width: 13%; max-width: 13%" class="bonus" >
                       <div class="d-flex fill-height align-center justify-center">
                           <strong class="dark-blue-color">{{ $t('orders.base.bonus') }}</strong>
                       </div>
                   </v-col>

                   <v-col v-if="userStore.hasPermissionTo('View prices')" :style="calculateHeaderColWidth('price')" class="price">
                       <div class="d-flex fill-height align-center justify-center">
                           <strong class="dark-blue-color">{{ $t('campaigns.unitPrice') }}</strong>
                       </div>
                   </v-col>

                   <v-col cols="12" :style="calculateHeaderColWidth('tax')" class="tax">
                       <div class="d-flex fill-height align-center justify-center">
                           <strong class="dark-blue-color">{{ $t('orders.base.vat') }}</strong>
                       </div>
                   </v-col>

                   <v-col v-if="userStore.hasPermissionTo('View prices')" :style="calculateHeaderColWidth('total')" class="total">
                       <div class="d-flex fill-height align-center justify-center">
                           <strong class="dark-blue-color">{{ $t('campaigns.value') }}</strong>
                       </div>
                   </v-col>
               </v-row>

               <v-list flat :class="insideDialog ? 'mr-n6' : ''" class="px-2 mt-0 mb-1 myprovet-shopping-cart-list" :height="height" :min-height="minHeight" :max-height="maxHeight">
                   <v-list-item-group :ripple="false" :class="insideDialog ? 'mr-6' : 'mr-4'" :style="insideDialog ? null : 'flex: 1 1 auto; height: 0px'">
                       <div v-for="(item, index) in getItems"  :key="item.uniqueId+'-'+index">
                           <v-list-item data-cy="shopping-cart-item" :ripple="false" :key="item.uniqueId" :class="getLineColor(item)" style="cursor: default">
                               <div class="myprovet-list-item-row">
                                   <div
                                       :style="calculateColWidth('designation')"
                                       :class="{
                                           'pl-9': !hasProductsToShow(item),
                                           'pl-16': item.isOffer && !item.isSeparate,
                                       }"
                                       class="myprovet-list-item-content product"
                                   >
                                       <div class="d-flex fill-height align-center justify-start">
                                           <template v-if="hasProductsToShow(item)">
                                               <v-icon v-if="!shouldDisplay(item.id, item.campaign_id)" class="mx-1 dark-blue-color" small @click="setExpandedProduct(item.id, item.campaign_id, true)" style="max-height: 30px"> mdi-chevron-down </v-icon>

                                               <v-icon v-else class="mx-1 dark-blue-color" small @click="setExpandedProduct(item.id, item.campaign_id, false)" style="max-height: 30px"> mdi-chevron-up </v-icon>
                                           </template>

                                           <v-icon v-if="item.isOffer" class="appbar-icon-button-small btn-bg-color-dark-yellow white--text mr-2">mdi-star-outline </v-icon>
                                           <v-icon v-else-if="item.campaign_id && isCampaignCollapsed(item)" :class="campaignIconStyle(item)">mdi-star-outline </v-icon>
                                           <v-icon v-else-if="!item.campaign_id && item.has_campaign" class="appbar-icon-button-small btn-bg-color-yellow dark-yellow-color mr-2">mdi-star-outline </v-icon>
                                           <v-icon v-else class="appbar-icon-button-small btn-bg-color-grey grey-color mr-2" style="visibility: hidden">mdi-star-outline </v-icon>
                                           <product-image :productCode="item.invoice_code" :imageSrc="item.image" :is-new="!!item.new" width="40" height="40" max-width="84" max-height="84" style="cursor: pointer; position: relative" @click="openProductDetails(item)"></product-image>

                                           <v-list-item-content @click="openProductDetails(item)" class="py-0 myprovet-list-item-content-clickable">
                                               <p class="shopping-cart-product-title" data-cy="cart-item-designation" v-text="item.designation"></p>
                                               <v-list-item-subtitle v-if="item.code" style="font-size: 10px; margin-bottom: -4px; line-height: 1.5">{{ item.code }}</v-list-item-subtitle>
                                           </v-list-item-content>
                                       </div>
                                   </div>

                                   <div v-if="!insideDialog" class="myprovet-list-item-content substitute" style="width: 7%; max-width: 7%">
                                       <div v-if="showSubstituteProductsColumn">
                                           <div v-if="hasSubstituteProducts(item)" class="d-flex fill-height align-center justify-center">
                                               <v-tooltip bottom open-on-hover content-class="myprovet-tooltip">
                                                   <template v-slot:activator="{ on, attrs }">
                                                       <v-icon
                                                           v-on="on"
                                                           v-bind="attrs"
                                                           small
                                                           class="appbar-icon-button-small myprovet-cart-item-button"
                                                           :class="!item.selected_substitute_product ? 'btn-bg-color-grey' : 'btn-bg-color-blue'"
                                                           style="cursor: pointer"
                                                           :disabled="summaryPage"
                                                           @click="authorizeReplacementDialogOpened(item, index)"
                                                       >
                                                           mdi-list-status
                                                       </v-icon>
                                                   </template>

                                                   <span>{{ $t('cart.viewSubstitutes') }}</span>
                                               </v-tooltip>

                                               <v-tooltip bottom open-on-hover content-class="myprovet-tooltip">
                                                   <template v-slot:activator="{ on, attrs }">
                                                       <v-icon small v-on="on" v-bind="attrs" :class="item.selected_substitute_product ? 'green-color' : 'dark-blue-color'" class="pl-0 ml-1">mdi-information</v-icon>
                                                   </template>

                                                   <span v-if="item.selected_substitute_product">
                                                       {{ $t('cart.authorizeSubstitution') }}<br /><strong>{{ item.selected_substitute_product.designation }}</strong>
                                                   </span>

                                                   <span v-else>{{ $t('cart.noSubstitute') }}</span>
                                               </v-tooltip>
                                           </div>

                                           <authorize-product-replacement-dialog
                                               v-if="isAuthorizeReplacementDialogOpen"
                                               :isAuthorizeReplacementDialogOpen="isAuthorizeReplacementDialogOpen"
                                               :selectedProduct="selectedProduct"
                                               @clear="onChangeAuthorizeReplacement(false, item)"
                                               @onClose="onCloseAuthorizeProductReplacementDialog"
                                           ></authorize-product-replacement-dialog>
                                       </div>
                                   </div>

                                   <div class="myprovet-list-item-content quantity" :style="calculateColWidth('quantity')">
                                       <div class="d-flex fill-height align-center justify-center">
                                           <quantity-counter
                                               v-if="!item.campaign_id"
                                               :value="item.quantity"
                                               :min="item.bonus > 0 ? 0 : 1"
                                               :max="normalOrderStore.maxQuantity"
                                               @input="onQuantityUpdated(item, $event)"
                                               @decrement="increment(item, -1)"
                                               @increment="increment(item, 1)"
                                               :hide-counter="summaryPage || branchStore.cannotPurchase"
                                               input-class="shopping-cart-input minimum-input-size"
                                               :disabled="summaryPage || branchStore.cannotPurchase"
                                           ></quantity-counter>

                                           <quantity-counter
                                               v-else-if="!item.isOffer"
                                               :key="quantityCounterKey"
                                               :is-active-campaign="true"
                                               :value="item.quantity"
                                               :min="item.min_quantity"
                                               :max="item.max_quantity"
                                               @input="tryUpdateCampaignQuantity(item, $event)"
                                               @input-remove-campaign="tryRemoveCampaign(item)"
                                               @decrement="updateCampaignQuantity(item, false)"
                                               @increment="updateCampaignQuantity(item, true)"
                                               :hide-counter="summaryPage || branchStore.cannotPurchase"
                                               input-class="shopping-cart-input minimum-input-size"
                                               :disabled="item.exceeding_quantity || branchStore.cannotPurchase ? true : false"
                                           ></quantity-counter>

                                           <quantity-counter
                                               v-else-if="item.isOffer && item.exceeding_quantity"
                                               :is-active-campaign="true"
                                               :value="item.quantity"
                                               :min="item.min_quantity"
                                               :max="maxQuantityCampaign(item.max_quantity)"
                                               input-class="shopping-cart-input minimum-input-size"
                                               :disabled="true"
                                               :hide-counter="true"
                                           ></quantity-counter>
                                       </div>
                                   </div>

                                   <div class="myprovet-list-item-content bonus" v-if="canViewBonusHeader" style="width: 13%; max-width: 13%">
                                       <div class="d-flex fill-height align-center justify-center">
                                           <p v-if="isBonusDiscount(item)" class="shopping-cart-product-title mt-5" data-cy="cart-item-discount-percentage">{{ item.discount }}%</p>

                                           <quantity-counter
                                               v-else
                                               :readonly="item.campaign_id != null  || !canChangeBonusQuantity"
                                               :hide-counter="summaryPage || item.campaign_id != null || !canChangeBonusQuantity || branchStore.cannotPurchase || normalOrderStore.checkIfPubArticle(item) "
                                               :hide-input="hideQuantityCounter(item) || branchStore.cannotPurchase || normalOrderStore.checkIfPubArticle(item)"
                                               :value="offerBonusQuantity(item)"
                                               :min="item.quantity > 0 ? 0 : 1"
                                               input-class="shopping-cart-input minimum-input-size"
                                               @input="onBonusQuantityUpdated(item, $event)"
                                               @increment="incrementBonus(item, 1)"
                                               @decrement="incrementBonus(item, -1)"
                                           ></quantity-counter>
                                       </div>
                                   </div>

                                   <div v-if="userStore.hasPermissionTo('View prices')" class="myprovet-list-item-content price" :style="calculateColWidth('price')">
                                       <div class="d-flex fill-height align-center justify-end cart-dialog-price" style="width: fit-content">
                                           <template v-if="item.discount">
                                               <v-list-item-content @click="openProductDetails(item)" class="py-0 shopping-cart-product-title text-center">
                                                   <p class="red--text text-decoration-strikethrough" style="font-size: 0.75rem !important">{{ formatPrice(item.price) }}</p>
                                                   <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem !important; margin-bottom: -4px; line-height: 1.5">{{ formatPrice(item.price - item.price * (item.discount / 100)) }}</v-list-item-subtitle>
                                               </v-list-item-content>
                                           </template>

                                           <template v-else-if="item.value">
                                               <v-list-item-content @click="openProductDetails(item)" class="py-0 shopping-cart-product-title text-center">
                                                   <p class="red--text text-decoration-strikethrough" style="font-size: 0.75rem !important">{{ formatPrice(item.price) }}</p>
                                                   <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem !important; margin-bottom: -4px; line-height: 1.5">{{ formatPrice(item.value) }}</v-list-item-subtitle>
                                               </v-list-item-content>
                                           </template>

                                           <template v-else>
                                               <p class="shopping-cart-product-title mt-5" data-cy="cart-item-price">{{ formatPrice(item.price) }}</p>
                                           </template>

                                           <product-discounts-icon v-if="userStore.hasPermissionTo('View discounts icon')" :product="item"></product-discounts-icon>
                                       </div>
                                   </div>

                                   <div class="myprovet-list-item-content tax" :style="calculateColWidth('tax')">
                                       <div class="d-flex fill-height align-center justify-end">
                                           <p class="shopping-cart-product-title mt-5" data-cy="cart-item-tax">{{ item.vat.tax }} %</p>
                                       </div>
                                   </div>

                                   <div v-if="userStore.hasPermissionTo('View prices')" class="myprovet-list-item-content total" :style="calculateColWidth('total')">
                                       <div class="d-flex fill-height align-center justify-end">
                                           <p class="shopping-cart-product-title mt-5" data-cy="cart-item-sub-total-price">{{ formatPrice(getProductSubTotalPrice(item)) }}</p>
                                       </div>
                                   </div>

                                   <div class="pl-0 myprovet-list-item-content" :style="calculateColWidth('actions')">
                                       <div v-if="!summaryPage" class="d-flex fill-height align-center justify-center">
                                           <div class="mr-0 myprovet-cart-item-button">
                                               <product-comment :product="item"></product-comment>
                                           </div>

                                           <div class="ml-2 myprovet-cart-item-button">
                                               <div v-if="!item.isOffer">
                                                   <v-tooltip v-if="item.campaign_id" bottom open-on-hover content-class="myprovet-tooltip">
                                                       <template v-slot:activator="{ on, attrs }">
                                                           <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color" @click="confirmCampaignRemoval(item)" v-on="on" v-bind="attrs" data-cy="remove-product"> mdi-delete-outline </v-icon>
                                                       </template>

                                                       <span>{{ $t('cart.removeCampaign') }}</span>
                                                   </v-tooltip>

                                                   <v-tooltip v-else bottom open-on-hover content-class="myprovet-tooltip">
                                                       <template v-slot:activator="{ on, attrs }">
                                                           <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color" @click="confirmItemRemoval(item)" v-on="on" v-bind="attrs" data-cy="remove-product"> mdi-delete-outline </v-icon>
                                                       </template>

                                                       <span>{{ $t('campaigns.removeProduct') }}</span>
                                                   </v-tooltip>
                                               </div>
                                           </div>
                                       </div>
                                   </div>
                               </div>
                           </v-list-item>

                           <v-divider v-if="index < getAmountOfItems - 1" :key="`${index}-divider-bottom`" class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>
                       </div>
                   </v-list-item-group>
               </v-list>
           </div>

           <div v-if="hasItems && !insideDialog && !summaryPage" class="additional-info">
               <v-row>
                   <v-col cols="12" md="8" sm="8">
                       <order-additional-info v-if="branchStore.branch && userStore.hasPermissionTo('Can access the additional information when creating an order')" :firstOrderDiscounts="firstOrderDiscounts" :optionsPosition="'bottom'"></order-additional-info>
                   </v-col>

                   <v-col v-if="userStore.hasPermissionTo('View prices')" cols="12" md="4" sm="4">
                       <v-row no-gutters>
                           <v-col class="d-flex align-center justify-end mr-16">
                               <strong class="dark-blue-color ma-0">{{ $t('cart.subtotal') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                               <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartSubtotal) }}</strong>
                           </v-col>
                       </v-row>

                       <v-row no-gutters class="mt-2">
                           <v-col class="d-flex align-center justify-end mr-16">
                               <strong class="dark-blue-color ma-0">{{ $t('cart.totalVat') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                               <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartIVA) }}</strong>
                           </v-col>
                       </v-row>

                       <v-row no-gutters class="mt-2">
                           <v-col class="d-flex align-center justify-end mr-16">
                               <strong class="total-price-color ma-0" style="font-size: 18px">{{ $t('orderDetails.total') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                               <strong class="total-price-color ma-0" style="font-size: 18px">{{ formatPrice(shoppingCartTotal) }}</strong>
                           </v-col>
                       </v-row>
                   </v-col>
               </v-row>

               <v-row class="mt-6">
                   <v-col cols="12" md="6" sm="6">
                       <v-select v-model="selectedAddress" :items="getBranchAddresses" item-text="address" :label="$t('orders.affinity.deliveryAddress')" data-cy="cart-address" :no-data-text="$t('orders.affinity.noAddresses')" prepend-icon="mdi-map-marker-outline" return-object hide-details dense class="address-input pt1">
                           <template slot="item" slot-scope="data">
                               <span data-cy="cart-address-item">{{ data.item.address }}</span>
                           </template>
                       </v-select>
                   </v-col>

                   <v-col cols="12" md="6" sm="6">
                       <v-text-field :maxlength="250" :counter="250" v-model.trim="observation" :label="$t('orders.affinity.observations')" clearable prepend-icon="mdi-comment-processing-outline" data-cy="cart-observation" dense class="order-observation"></v-text-field>
                   </v-col>
               </v-row>
           </div>

           <div v-if="hasItems && summaryPage">
               <v-row class="my-0">
                   <v-col cols="12" md="8" sm="8" class="additional-info-summary" data-cy="additional-info-summary-page">
                       <v-row no-gutters>
                           <v-col cols="12" md="12" sm="12" class="px-2">
                               <div class="d-flex fill-height align-center justify-start">
                                   <strong class="dark-blue-color">{{ $t('orders.affinity.deliveryAddress') }}</strong>
                               </div>
                           </v-col>
                       </v-row>

                       <v-row no-gutters>
                           <v-col cols="12" md="12" sm="12" class="px-2">
                               <div class="d-flex fill-height align-center justify-start">
                                   <p class="font-size-md description-text" data-cy="summary-address">{{ selectedAddress }}</p>
                               </div>
                           </v-col>
                       </v-row>

                       <v-row no-gutters class="mt-2">
                           <v-col cols="12" md="12" sm="12" class="px-2">
                               <div class="d-flex fill-height align-center justify-start">
                                   <strong class="dark-blue-color">{{ $t('orders.affinity.observations') }}</strong>
                               </div>
                           </v-col>
                       </v-row>

                       <v-row no-gutters>
                           <v-col cols="12" md="12" sm="12" class="px-2">
                               <div class="d-flex fill-height align-center justify-start">
                                   <p class="font-size-md description-text" data-cy="summary-observations">{{ observation }}</p>
                               </div>
                           </v-col>
                       </v-row>

                       <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                           <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                               <v-checkbox v-model="ckBudgetRequest" :label="$t('orders.additionalInfo.budgetRequest')" class="dark-gray-color-label" hide-details disabled data-cy="summary-budget-cbx"></v-checkbox>
                           </v-col>

                           <v-col cols="12" md="8" sm="8" class="px-2 py-0"> </v-col>
                       </v-row>

                       <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                           <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                               <v-checkbox v-model="ckScheduleOrder" :label="$t('orders.additionalInfo.scheduleOrder')" class="dark-gray-color-label" hide-details disabled data-cy="summary-schedule-cbx"></v-checkbox>
                           </v-col>

                           <v-col v-if="ckScheduleOrder" cols="12" md="4" sm="4" class="px-2 py-0 large-side-field">
                               <date-picker-menu v-if="ckScheduleOrder" :value="scheduleOrderDate" disabled hide-details :inOrderDetails="true" data-cy="summary-schedule-date"> </date-picker-menu>
                           </v-col>
                       </v-row>

                       <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                           <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                               <v-checkbox v-model="ckLaboratoryOrder" :label="$t('orders.additionalInfo.laboratoryOrder')" class="dark-gray-color-label" hide-details disabled data-cy="summary-laboratory"></v-checkbox>
                           </v-col>
                       </v-row>

                       <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                           <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                               <v-checkbox v-model="ckFirstOrder" :label="$t('orders.additionalInfo.firstOrder')" class="dark-gray-color-label" hide-details disabled data-cy="summary-first-order-cbx"></v-checkbox>
                           </v-col>

                           <v-col v-if="ckFirstOrder" cols="12" md="2" sm="2" class="px-2 py-0 large-side-field">
                               <v-text-field v-model="firstOrderDiscount.percentage" :label="$t('cart.discountPercent')" style="margin-top: 3.5px !important" disabled hide-details data-cy="summary-first-order-value"> </v-text-field>
                           </v-col>
                       </v-row>
                   </v-col>

                   <v-col v-if="userStore.hasPermissionTo('View prices')" cols="12" md="4" sm="4">
                       <v-row no-gutters>
                           <v-col class="d-flex align-center justify-end mr-16">
                               <strong class="dark-blue-color ma-0">{{ $t('cart.subtotal') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                               <strong class="dark-blue-color ma-0" data-cy="summary-sub-total-value">{{ formatPrice(shoppingCartSubtotal) }}</strong>
                           </v-col>
                       </v-row>

                       <v-row no-gutters class="mt-2">
                           <v-col class="d-flex align-center justify-end mr-16">
                               <strong class="dark-blue-color ma-0">{{ $t('cart.totalVat') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                               <strong class="dark-blue-color ma-0" data-cy="summary-tax-value">{{ formatPrice(shoppingCartIVA) }}</strong>
                           </v-col>
                       </v-row>

                       <v-row no-gutters class="mt-2">
                           <v-col class="d-flex align-center justify-end mr-16">
                               <strong class="total-price-color ma-0" style="font-size: 18px">{{ $t('orderDetails.total') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                               <strong class="total-price-color ma-0" style="font-size: 18px" data-cy="summary-total-value">{{ formatPrice(shoppingCartTotal) }}</strong>
                           </v-col>
                       </v-row>
                   </v-col>
               </v-row>
           </div>

           <div v-if="hasItems && insideDialog && userStore.hasPermissionTo('View prices')">
               <v-row class="mt-8">
                   <v-col cols="12" md="7" sm="7">
                   </v-col>

                   <v-col cols="12" md="5" sm="5">
                       <v-row no-gutters>
                           <v-col class="d-flex align-left justify-end mr-16">
                               <v-tooltip  bottom open-on-hover content-class="myprovet-tooltip" v-if="userStore.hasPermissionTo('Change bonus quantity (as propec)') ">
                                   <template v-slot:activator="{ on, attrs }">
                                       <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color total-by-labs" @click="openTotalByLabs" v-on="on" v-bind="attrs" data-cy="remove-product">  mdi-file-table-box-multiple</v-icon>
                                   </template>
                                   <span>{{ $t('cart.totalsByLab') }}</span>
                               </v-tooltip><strong class="dark-blue-color ma-0">{{ $t('cart.subtotal') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size ml-8">
                               <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartSubtotal) }}</strong>
                           </v-col>
                       </v-row>

                       <v-row no-gutters class="mt-2">
                           <v-col class="d-flex align-left justify-end mr-16">
                               <strong class="dark-blue-color ma-0">{{ $t('cart.totalVat') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size ml-8">
                               <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartIVA) }}</strong>
                           </v-col>
                       </v-row>

                       <v-row no-gutters class="mt-2">
                           <v-col class="d-flex align-left justify-end mr-16">
                               <strong class="total-price-color ma-0" style="font-size: 18px">{{ $t('orderDetails.total') }}</strong>
                           </v-col>
                           <v-col class="d-flex justify-end flex-grow-0 minimum-field-size ml-8">
                               <strong class="total-price-color ma-0" style="font-size: 18px">{{ formatPrice(shoppingCartTotal) }}</strong>
                           </v-col>
                       </v-row>
                   </v-col>
               </v-row>
           </div>

           <product-campaign-form
               v-if="isDetailedCampaignDialogOpen"
               :key="selectedProduct.id"
               :isOpen="isDetailedCampaignDialogOpen"
               :product="selectedProduct"
               :campaigns="manipulatedProductCampaigns"
               :account-blocked-message="accountBlockedMessage"
               :currentSearchTerm="null"
               @input="onCloseDetailedCampaignDialog()"
               @check-applied-campaigns="checkAppliedCampaigns()"
               @create-new-original-campaign="createNewOriginalCampaign($event)"
               @show-campaigns="onClickProductCampaign"
               :parentPage="parentPage"
           ></product-campaign-form>

           <app-dialog v-if="isProductDetailsDialogOpen" :isProductDetails="true" :value="isProductDetailsDialogOpen" @input="isProductDetailsDialogOpen = $event" :title="$t('orders.base.productSheet')" width="55vw" :isResponsive="true" :showActions="false">
               <template v-slot:content>
                   <product-details :parentPage="parentPage" :product="selectedProduct" :key="selectedProduct.id" @change-product="selectedProduct = $event" @show-campaigns="onClickProductCampaign" :summary-page="summaryPage"></product-details>
               </template>
           </app-dialog>

           <confirmation-dialog :value="isRemoveCampaignDialogOpen" @onSuccess="remove(selectedProduct)" @onCancel="isRemoveCampaignDialogOpen = false" :title="$t('layout.attention')" :question="$t('orders.base.removeCampaignQuestion')"> </confirmation-dialog>
           <confirmation-dialog :value="isConfirmDialogOpen" @onSuccess="remove(selectedProduct)" @onCancel="isConfirmDialogOpen = false" :title="$t('layout.attention')" :question="$t('campaigns.removeProductQuestion')"> </confirmation-dialog>
       </div>
       <div :class="{ 'fill-height d-flex flex-column wrap-shopping-cart': !insideDialog }" v-else >
               <template v-if="!hasItems">
                   <div class="d-flex align-center justify-center mt-16">
                       <v-icon class="appbar-icon-button btn-bg-color-grey grey-color"> mdi-cart-variant </v-icon>
                   </div>

                   <div class="d-flex align-center justify-center mt-4 mb-8">
                       <p class="text-h6 grey-color" data-cy="empty-shopping-cart-text">{{ $t('cart.empty') }}</p>
                   </div>
               </template>
       <div v-else :class="getItems.length>5?'mt-2 fill-height myprovet-shopping-cart-list vh-limit':'mt-2 fill-height myprovet-shopping-cart-list'">
                   <div  v-for="(item, index) in getItems" :key="item.uniqueId+index+item.code">
                       <v-divider v-if="index==0 && !item.isOffer" :key="`${index}-divider-0`" class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>
                       <v-divider v-if="index > 0 && !item.isOffer" :key="`${index}-prod-divider1`" class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>
                   <v-row :class="item.isOffer?'row-offer':''">
                       <v-col class="d-flex flex-column col pt-0 pb-0">
                           <v-row class="d-flex align-start pt-0 pb-0">
                               <v-col class="col col-3 pt-0 pb-0 wrap-icon-campaign">
                                   <div class="d-flex fill-height align-center justify-start">
                                               <template v-if="hasProductsToShow(item)">
                                                   <v-icon v-if="!shouldDisplay(item.id, item.campaign_id)" class="mx-1 dark-blue-color" small @click="setExpandedProduct(item.id, item.campaign_id, true)" style="max-height: 30px"> mdi-chevron-down </v-icon>

                                                   <v-icon v-else class="mx-1 dark-blue-color" small @click="setExpandedProduct(item.id, item.campaign_id, false)" style="max-height: 30px"> mdi-chevron-up </v-icon>
                                               </template>

                                               <v-icon v-if="item.isOffer" class="appbar-icon-button-small btn-bg-color-dark-yellow white--text mr-2">mdi-star-outline </v-icon>
                                               <v-icon v-else-if="item.campaign_id && isCampaignCollapsed(item)" :class="campaignIconStyle(item)">mdi-star-outline </v-icon>
                                               <v-icon v-else-if="!item.campaign_id && item.has_campaign" class="appbar-icon-button-small btn-bg-color-yellow dark-yellow-color mr-2">mdi-star-outline </v-icon>
                                               <v-icon v-else class="appbar-icon-button-small btn-bg-color-grey grey-color mr-2 n-wrap-icon-campaign" style="visibility: hidden">mdi-star-outline </v-icon>
                                           <product-image :productCode="item.invoice_code" :imageSrc="item.image" :is-new="!!item.new" width="84" height="84" max-width="84" max-height="84" style="cursor: pointer; position: relative" @click="openProductDetails(item)"></product-image>
                                   </div>
                               </v-col>
                               <v-col class="col col-9 pt-0 pb-0">
                                   <v-row class="d-flex align-start pt-0 pb-0">
                                       <v-col class="col col-12 pt-0 pb-0">
                                           <div class="pl-0 datatable-designation-text" style="cursor: pointer" @click="openProductDetails(item)">
                                               {{ item.designation }}
                                           </div>
                                       </v-col>
                                       <v-col class="col col-12 pt-0 pb-0">
                                           <div class="pl-0 datatable-lable-text laboratory">
                                               {{ item.code}}
                                           </div>
                                       </v-col>
                                       <v-col class="col col-12 pt-0 pb-0 stock">
                                           <div class="price" v-if="userStore.hasPermissionTo('View prices')">
                                               <template v-if="item.discount">
                                                   <v-list-item-content class="py-0">
                                                       <div class="flex-column" style="line-height: 18px">
                                                           <p class="red--text text-decoration-strikethrough mb-0" style="font-size: 0.75rem">{{ formatPrice(item.price) }}</p>
                                                           <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem">{{ formatPrice(item.price - item.price * (item.discount / 100)) }}</v-list-item-subtitle>
                                                       </div>
                                                   </v-list-item-content>
                                               </template>

                                               <template v-else-if="item.value">
                                                   <v-list-item-content class="py-0">
                                                       <div class="flex-column" style="line-height: 18px">
                                                           <p class="red--text text-decoration-strikethrough mb-0" style="font-size: 0.75em">{{ formatPrice(item.price) }}</p>
                                                           <v-list-item-subtitle class="green--text text--darken-2" style="font-size: 0.75rem">{{ formatPrice(item.value) }}</v-list-item-subtitle>
                                                       </div>
                                                   </v-list-item-content>
                                               </template>

                                               <template v-else>
                                                   {{ formatPrice(item.price) }}
                                               </template>
                                               <product-discounts-icon v-if="userStore.hasPermissionTo('View discounts icon')" :product="item"></product-discounts-icon>
                                           </div>
                                           <div v-if="!insideDialog" class="substitute-product">
                                               <div v-if="showSubstituteProductsColumn">
                                                   <div v-if="hasSubstituteProducts(item)" class="d-flex fill-height align-center justify-center">
                                                       <v-tooltip bottom open-on-hover content-class="myprovet-tooltip">
                                                           <template v-slot:activator="{ on, attrs }">
                                                               <v-icon
                                                                   v-on="on"
                                                                   v-bind="attrs"
                                                                   small
                                                                   class="appbar-icon-button-small myprovet-cart-item-button"
                                                                   :class="!item.selected_substitute_product ? 'btn-bg-color-grey' : 'btn-bg-color-blue'"
                                                                   style="cursor: pointer"
                                                                   :disabled="summaryPage"
                                                                   @click="authorizeReplacementDialogOpened(item, index)"
                                                               >
                                                                   mdi-list-status
                                                               </v-icon>
                                                           </template>

                                                           <span>{{ $t('cart.viewSubstitutes') }}</span>
                                                       </v-tooltip>

                                                       <v-tooltip bottom open-on-hover content-class="myprovet-tooltip">
                                                           <template v-slot:activator="{ on, attrs }">
                                                               <v-icon small v-on="on" v-bind="attrs" :class="item.selected_substitute_product ? 'green-color' : 'dark-blue-color'" class="pl-0 ml-1">mdi-information</v-icon>
                                                           </template>

                                                           <span v-if="item.selected_substitute_product"
                                                           >{{ $t('cart.authorizeSubstitution') }}<br /><strong>{{ item.selected_substitute_product.designation }}</strong></span
                                                           >
                                                           <span v-else>{{ $t('cart.noSubstitute') }}</span>
                                                       </v-tooltip>
                                                   </div>

                                                   <authorize-product-replacement-dialog
                                                       v-if="isAuthorizeReplacementDialogOpen"
                                                       :isAuthorizeReplacementDialogOpen="isAuthorizeReplacementDialogOpen"
                                                       :selectedProduct="selectedProduct"
                                                       @clear="onChangeAuthorizeReplacement(false, item)"
                                                       @onClose="onCloseAuthorizeProductReplacementDialog"
                                                   ></authorize-product-replacement-dialog>
                                               </div>
                                           </div>
                                           <div class="btn-actions">
                                               <div v-if="!item.isOffer" class="mr-1 myprovet-cart-item-button">
                                                       <v-tooltip v-if="item.campaign_id" bottom open-on-hover content-class="myprovet-tooltip">
                                                           <template v-slot:activator="{ on, attrs }">
                                                               <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color" @click="confirmCampaignRemoval(item)" v-on="on" v-bind="attrs" data-cy="remove-product"> mdi-delete-outline </v-icon>
                                                           </template>

                                                           <span>{{ $t('cart.removeCampaign') }}</span>
                                                       </v-tooltip>

                                                       <v-tooltip v-else bottom open-on-hover content-class="myprovet-tooltip">
                                                           <template v-slot:activator="{ on, attrs }">
                                                               <v-icon small class="appbar-icon-button-small btn-bg-color-blue dark-blue-color" @click="confirmItemRemoval(item)" v-on="on" v-bind="attrs" data-cy="remove-product"> mdi-delete-outline </v-icon>
                                                           </template>

                                                           <span>{{ $t('campaigns.removeProduct') }}</span>
                                                       </v-tooltip>
                                               </div>
                                               <div class="mr-1 myprovet-cart-item-button">
                                                   <product-comment v-if="item.quantity > 0" :product="item"></product-comment>
                                               </div>

                                           </div>
                                           <!--<v-tooltip bottom open-on-hover content-class="myprovet-tooltip" data-cy="stocks">
                                                                           <template v-slot:activator="{ on, attrs }">
                                                                               <v-icon style="" :color="isAvailable(product) ? 'green' : 'red'" v-on="on" v-bind="attrs">mdi-circle</v-icon>
                                                                           </template>

                                                                           <span>{{ isAvailable(product) ? $t('orders.base.stockAvailable') : $t('orders.base.confirmStock') }}</span>
                                                                       </v-tooltip>
                                                                       <product-additional-info :product="product"> </product-additional-info>-->
                                       </v-col>
                                   </v-row>
                               </v-col>
                               <v-col class="col col-12 pt-0 pb-0">
                               </v-col>
                               <v-col class="col col-12 pt-0 pb-0">
                                   <v-row :class="hideQuantityCounter(item)?'d-flex align-start pt-0 pb-0 no-label-qty':'d-flex align-start pt-0 pb-0 no-input-bonus'">
                                       <v-col class="col col-6 pt-0 pb-0 pl-0">
                                           <div class="col pt-0 pb-0 label qty" v-if="!item.campaign_id || !item.isOffer || item.isOffer && item.exceeding_quantity">{{ $t('orders.base.quantity') }}</div>
                                               <quantity-counter
                                                   v-if="!item.campaign_id"
                                                   :value="item.quantity"
                                                   :min="item.bonus > 0 ? 0 : 1"
                                                   :max="normalOrderStore.maxQuantity"
                                                   @input="onQuantityUpdated(item, $event)"
                                                   @decrement="increment(item, -1)"
                                                   @increment="increment(item, 1)"
                                                   :hide-counter="summaryPage || branchStore.cannotPurchase"
                                                   input-class="shopping-cart-input minimum-input-size"
                                                   :disabled="summaryPage || branchStore.cannotPurchase"
                                               ></quantity-counter>

                                               <quantity-counter
                                                   v-else-if="!item.isOffer"
                                                   :is-active-campaign="true"
                                                   :value="item.quantity"
                                                   :min="item.min_quantity"
                                                   :max="item.max_quantity"
                                                   @input="tryUpdateCampaignQuantity(item, $event)"
                                                   @input-remove-campaign="tryRemoveCampaign(item)"
                                                   @decrement="updateCampaignQuantity(item, false)"
                                                   @increment="updateCampaignQuantity(item, true)"
                                                   :hide-counter="summaryPage || branchStore.cannotPurchase"
                                                   input-class="shopping-cart-input minimum-input-size"
                                                   :disabled="item.exceeding_quantity || branchStore.cannotPurchase ? true : false"
                                               ></quantity-counter>

                                               <quantity-counter
                                                   v-else-if="item.isOffer && item.exceeding_quantity"
                                                   :is-active-campaign="true"
                                                   :value="item.quantity"
                                                   :min="item.min_quantity"
                                                   :max="maxQuantityCampaign(item.max_quantity)"
                                                   input-class="shopping-cart-input minimum-input-size"
                                                   :disabled="true"
                                                   :hide-counter="true"
                                               ></quantity-counter>
                                       </v-col>
                                       <v-col class="col col-6 pt-0 pb-0 pl-0">
                                           <div class="col pt-0 pb-2 empty"></div>
                                           <div class="col pt-0 pb-2  pl-0 pr-0 label lf">
                                               <div class="l-sub">{{ $t('orders.base.vat') }}</div>
                                               <div class="l-sub-value">{{item.vat.tax}} %</div>
                                           </div>
                                       </v-col>
                                   </v-row>
                               </v-col>
                               <v-col class="col col-12 pt-0 pb-0">
                                   <v-row :class="hideQuantityCounter(item)|| !hideQuantityCounter(item)?'d-flex align-start pt-0 pb-0 no-input-qty':'d-flex align-start pt-0 pb-0 no-label-bn'">
                                       <v-col class="col col-6 pt-0 pb-0 pl-0 wrap-bonus" v-if="canViewBonusHeader ">
                                           <div class="col pt-0 pb-0 label bonus" v-if="!hideQuantityCounter(item)">{{ $t('orders.base.bonus') }}</div>
                                               <p v-if="isBonusDiscount(item)" class="shopping-cart-product-title mt-5" data-cy="cart-item-discount-percentage">{{ item.discount }}%</p>
                                               <quantity-counter
                                                   v-else
                                                   :readonly="item.campaign_id != null || !canChangeBonusQuantity || branchStore.cannotPurchase"
                                                   :hide-counter="summaryPage || item.campaign_id != null || !canChangeBonusQuantity || branchStore.cannotPurchase"
                                                   :hide-input="hideQuantityCounter(item) || branchStore.cannotPurchase"
                                                   :value="offerBonusQuantity(item)"
                                                   :min="item.quantity > 0 ? 0 : 1"
                                                   input-class="shopping-cart-input minimum-input-size"
                                                   @input="onBonusQuantityUpdated(item, $event)"
                                                   @increment="incrementBonus(item, 1)"
                                                   @decrement="incrementBonus(item, -1)"
                                               ></quantity-counter>
                                       </v-col>
                                       <v-col class="col col-6 pt-0 pb-0 pl-0">
                                           <div :class="!hideQuantityCounter(item)?'col pt-0 pb-2 empty':''"></div>
                                           <div class="col pt-0 pb-2  pl-0 pr-0 label lf ">
                                               <div class="l-sub">{{ $t('campaigns.value') }}</div>
                                               <div class="l-sub-value" v-if="userStore.hasPermissionTo('View prices')">

                                                   <p class="shopping-cart-product-title " data-cy="cart-item-sub-total-price">{{ formatPrice(getProductSubTotalPrice(item)) }}</p>


                                               </div>
                                           </div>
                                       </v-col>
                                   </v-row>
                               </v-col>

                           </v-row>
                       </v-col>
                   </v-row>

                   <!--<v-divider v-if="index == getAmountOfItems -1" :key="`${index}-divider1`" class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>-->
                   </div>
           </div>
           <div v-if="hasItems && !insideDialog && !summaryPage">
                   <v-row >
                       <v-divider class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>
                       <v-col cols="12" md="8" sm="8" v-if="!isMobile">
                           <order-additional-info v-if="branchStore.branch && userStore.hasPermissionTo('Can access the additional information when creating an order')" :firstOrderDiscounts="firstOrderDiscounts" :optionsPosition="'bottom'"></order-additional-info>
                       </v-col>

                       <v-col v-if="userStore.hasPermissionTo('View prices')" cols="12" md="4" sm="4" :class="isMobile?'mt-6':''">
                           <v-row no-gutters>
                               <v-col class="d-flex align-center justify-end mr-16">
                                   <strong class="dark-blue-color ma-0">{{ $t('cart.subtotal') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                                   <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartSubtotal) }}</strong>
                               </v-col>
                           </v-row>

                           <v-row no-gutters class="mt-2">
                               <v-col class="d-flex align-center justify-end mr-16">
                                   <strong class="dark-blue-color ma-0">{{ $t('cart.totalVat') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                                   <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartIVA) }}</strong>
                               </v-col>
                           </v-row>

                           <v-row no-gutters class="mt-2">
                               <v-col class="d-flex align-center justify-end mr-16">
                                   <strong class="total-price-color ma-0" style="font-size: 18px">{{ $t('orderDetails.total') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                                   <strong class="total-price-color ma-0" style="font-size: 18px">{{ formatPrice(shoppingCartTotal) }}</strong>
                               </v-col>
                           </v-row>
                       </v-col>
                   </v-row>
                   <v-col class="mt-6" cols="12" md="8" sm="8" v-if="isMobile">
                           <order-additional-info v-if="branchStore.branch && userStore.hasPermissionTo('Can access the additional information when creating an order')" :firstOrderDiscounts="firstOrderDiscounts" :optionsPosition="'bottom'"></order-additional-info>
                       </v-col>
                   <v-row class="mt-6" >
                       <v-col cols="12" md="6" sm="6">
                           <v-select v-model="selectedAddress" :items="getBranchAddresses" item-text="address" :label="$t('orders.affinity.deliveryAddress')" data-cy="cart-address" :no-data-text="$t('orders.affinity.noAddresses')" prepend-icon="mdi-map-marker-outline" return-object hide-details dense class="address-input pt1">
                               <template slot="item" slot-scope="data">
                                   <span data-cy="cart-address-item">{{ data.item.address }}</span>
                               </template>
                           </v-select>
                       </v-col>

                       <v-col cols="12" md="6" sm="6">
                           <v-text-field :maxlength="250" :counter="250" v-model.trim="observation" :label="$t('orders.affinity.observations')" clearable prepend-icon="mdi-comment-processing-outline" data-cy="cart-observation" dense class="order-observation"></v-text-field>
                       </v-col>
                   </v-row>
               </div>

               <div v-if="hasItems && summaryPage">
                   <v-row class="my-0">
                       <v-col cols="12" md="8" sm="8" class="additional-info-summary" data-cy="additional-info-summary-page">
                           <v-row no-gutters>
                               <v-col cols="12" md="12" sm="12" class="px-2">
                                   <div class="d-flex fill-height align-center justify-start">
                                       <strong class="dark-blue-color">{{ $t('orders.affinity.deliveryAddress') }}</strong>
                                   </div>
                               </v-col>
                           </v-row>

                           <v-row no-gutters>
                               <v-col cols="12" md="12" sm="12" class="px-2">
                                   <div class="d-flex fill-height align-center justify-start">
                                       <p class="font-size-md description-text" data-cy="summary-address">{{ selectedAddress }}</p>
                                   </div>
                               </v-col>
                           </v-row>

                           <v-row no-gutters class="mt-2">
                               <v-col cols="12" md="12" sm="12" class="px-2">
                                   <div class="d-flex fill-height align-center justify-start">
                                       <strong class="dark-blue-color">{{ $t('orders.affinity.observations') }}</strong>
                                   </div>
                               </v-col>
                           </v-row>

                           <v-row no-gutters>
                               <v-col cols="12" md="12" sm="12" class="px-2">
                                   <div class="d-flex fill-height align-center justify-start">
                                       <p class="font-size-md description-text" data-cy="summary-observations">{{ observation }}</p>
                                   </div>
                               </v-col>
                           </v-row>

                           <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                               <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                                   <v-checkbox v-model="ckBudgetRequest" :label="$t('orders.additionalInfo.budgetRequest')" class="dark-gray-color-label" hide-details disabled data-cy="summary-budget-cbx"></v-checkbox>
                               </v-col>

                               <v-col cols="12" md="8" sm="8" class="px-2 py-0"> </v-col>
                           </v-row>

                           <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                               <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                                   <v-checkbox v-model="ckScheduleOrder" :label="$t('orders.additionalInfo.scheduleOrder')" class="dark-gray-color-label" hide-details disabled data-cy="summary-schedule-cbx"></v-checkbox>
                               </v-col>

                               <v-col v-if="ckScheduleOrder" cols="12" md="4" sm="4" class="px-2 py-0 large-side-field">
                                   <date-picker-menu v-if="ckScheduleOrder" :value="scheduleOrderDate" disabled hide-details :inOrderDetails="true" data-cy="summary-schedule-date"> </date-picker-menu>
                               </v-col>
                           </v-row>

                           <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                               <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                                   <v-checkbox v-model="ckLaboratoryOrder" :label="$t('orders.additionalInfo.laboratoryOrder')" class="dark-gray-color-label" hide-details disabled data-cy="summary-laboratory"></v-checkbox>
                               </v-col>
                           </v-row>

                           <v-row v-if="userStore.hasPermissionTo('Can access the additional information when creating an order')" no-gutters class="order-additional-info-row">
                               <v-col cols="12" md="6" sm="6" class="px-2 py-0">
                                   <v-checkbox v-model="ckFirstOrder" :label="$t('orders.additionalInfo.firstOrder')" class="dark-gray-color-label" hide-details disabled data-cy="summary-first-order-cbx"></v-checkbox>
                               </v-col>

                               <v-col v-if="ckFirstOrder" cols="12" md="2" sm="2" class="px-2 py-0 large-side-field">
                                   <v-text-field v-model="firstOrderDiscount.percentage" :label="$t('cart.discountPercent')" style="margin-top: 3.5px !important" disabled hide-details data-cy="summary-first-order-value"> </v-text-field>
                               </v-col>
                           </v-row>
                       </v-col>

                       <v-col v-if="userStore.hasPermissionTo('View prices')" cols="12" md="4" sm="4">
                           <v-row no-gutters>
                               <v-col class="d-flex align-center justify-end mr-16">
                                   <strong class="dark-blue-color ma-0">{{ $t('cart.subtotal') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                                   <strong class="dark-blue-color ma-0" data-cy="summary-sub-total-value">{{ formatPrice(shoppingCartSubtotal) }}</strong>
                               </v-col>
                           </v-row>

                           <v-row no-gutters class="mt-2">
                               <v-col class="d-flex align-center justify-end mr-16">
                                   <strong class="dark-blue-color ma-0">{{ $t('cart.totalVat') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                                   <strong class="dark-blue-color ma-0" data-cy="summary-tax-value">{{ formatPrice(shoppingCartIVA) }}</strong>
                               </v-col>
                           </v-row>

                           <v-row no-gutters class="mt-2">
                               <v-col class="d-flex align-center justify-end mr-16">
                                   <strong class="total-price-color ma-0" style="font-size: 18px">{{ $t('orderDetails.total') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size">
                                   <strong class="total-price-color ma-0" style="font-size: 18px" data-cy="summary-total-value">{{ formatPrice(shoppingCartTotal) }}</strong>
                               </v-col>
                           </v-row>
                       </v-col>
                   </v-row>
               </div>

               <div v-if="hasItems && insideDialog && userStore.hasPermissionTo('View prices')">
                   <v-row class="mt-0 mb-8">
                       <v-divider  class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>
                       <v-col cols="12" md="7" sm="7" v-if="!isMobile"> </v-col>

                       <v-col cols="12" md="5" sm="5">
                           <v-row no-gutters>
                               <v-col class="d-flex align-left justify-end mr-16">
                                   <strong class="dark-blue-color ma-0">{{ $t('cart.subtotal') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size ml-8">
                                   <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartSubtotal) }}</strong>
                               </v-col>
                           </v-row>

                           <v-row no-gutters class="mt-2">
                               <v-col class="d-flex align-left justify-end mr-16">
                                   <strong class="dark-blue-color ma-0">{{ $t('cart.totalVat') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size ml-8">
                                   <strong class="dark-blue-color ma-0">{{ formatPrice(shoppingCartIVA) }}</strong>
                               </v-col>
                           </v-row>

                           <v-row no-gutters class="mt-2">
                               <v-col class="d-flex align-left justify-end mr-16">
                                   <strong class="total-price-color ma-0" style="font-size: 18px">{{ $t('orderDetails.total') }}</strong>
                               </v-col>
                               <v-col class="d-flex justify-end flex-grow-0 minimum-field-size ml-8">
                                   <strong class="total-price-color ma-0" style="font-size: 18px">{{ formatPrice(shoppingCartTotal) }}</strong>
                               </v-col>
                           </v-row>
                       </v-col>
                   </v-row>
               </div>

               <product-campaign-form
               v-if="isDetailedCampaignDialogOpen"
               :key="selectedProduct.id"
               :isOpen="isDetailedCampaignDialogOpen"
               :product="selectedProduct"
               :campaigns="manipulatedProductCampaigns"
               :account-blocked-message="accountBlockedMessage"
               :currentSearchTerm="null"
               @input="onCloseDetailedCampaignDialog()"
               @check-applied-campaigns="checkAppliedCampaigns()"
               @create-new-original-campaign="createNewOriginalCampaign($event)"
               @show-campaigns="onClickProductCampaign"
               :parentPage="parentPage"
           ></product-campaign-form>

               <app-dialog v-if="isProductDetailsDialogOpen" :isProductDetails="true" :value="isProductDetailsDialogOpen" @input="isProductDetailsDialogOpen = $event" :title="$t('orders.base.productSheet')" width="55vw" :isResponsive="true" :showActions="false">
                   <template v-slot:content>
                       <product-details :parentPage="parentPage" :product="selectedProduct" :key="selectedProduct.id" @change-product="selectedProduct = $event" @show-campaigns="onClickProductCampaign" :summary-page="summaryPage"></product-details>
                   </template>
               </app-dialog>

               <confirmation-dialog :value="isRemoveCampaignDialogOpen" @onSuccess="remove(selectedProduct)" @onCancel="isRemoveCampaignDialogOpen = false" :title="$t('layout.attention')" :question="$t('orders.base.removeCampaignQuestion')"> </confirmation-dialog>
               <confirmation-dialog :value="isConfirmDialogOpen" @onSuccess="remove(selectedProduct)" @onCancel="isConfirmDialogOpen = false" :title="$t('layout.attention')" :question="$t('campaigns.removeProductQuestion')"> </confirmation-dialog>
       </div>
           <v-dialog
               v-model="isOfferTypeDialogOpen"
               persistent
               max-width="380"
               >
                   <v-card class="myprovet-dialog-card select-offer-type" :max-height="200" :height="160" data-cy="app-dialog-card">
                   <!--<v-card-title v-if="!isAlertDialog" class="d-flex justify-space-between align-center title-text dark-blue-color mx-1 px-6" data-cy="app-dialog-title">
                       Selecionar a origem do bónus
                       <v-spacer></v-spacer>
                   </v-card-title>-->
                   <v-card-text class="px-6 pb-2 pt-6">
                       <v-select
                       style="width:300px; font-size: 1rem!important;"
                       :items="bonusTypes"
                       v-model="currentOfferType"
                       :label="$t('orders.base.selectBonusType')"
                       @change="setBonusTypes"
                       ></v-select>
                   </v-card-text>
               </v-card>
           </v-dialog>
           <total-by-labs-dialog  :value="isTotalByLabsDialogOpen"  @input="isTotalByLabsDialogOpen = $event"  @onCancel="isTotalByLabsDialogOpen= false" width="50vw" max-height="80vh"> </total-by-labs-dialog>
   </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useBranchStore } from '@/Stores/branch';
import { useUserStore } from '@/Stores/user';
import { useAppStore } from '@/Stores/app';
import { useNormalOrderStore } from '@/Stores/normalorder';
import { axios } from '@/Services/Backend';
import { i18n } from '@/i18n';
import TotalByLabsDialog from '../../Dialog/TotalByLabsDialog.vue';

export default {
 components: { TotalByLabsDialog },
   props: {
       hasItems: Boolean,
       insideDialog: Boolean,
       summaryPage: Boolean,
       height: String,
       minHeight: String,
       maxHeight: String,
       firstOrderDiscounts: Array,
       accountBlockedMessage: String,
   },

   data: () => ({
       isLoading: false,

       isAuthorizeReplacementDialogOpen: false,
       isProductDetailsDialogOpen: false,
       isConfirmDialogOpen: false,
       isRemoveCampaignDialogOpen: false,
       isTotalByLabsDialogOpen:false,

       ckAuthorizeReplacement: false,
       disabledAuthorizeReplacement: true,
       authorizeReplacement: null,

       activeIndex: null,
       comment: null,

       selectedProduct: {},

       expandedProducts: [],
       productCampaigns: [],
       originalProductCampaigns: [],
       isDetailedCampaignDialogOpen: false,
       manipulatedProductCampaigns: [],

       rules: {
           percentageRule: (value) => (value && value >= 0 && value <= 100) || i18n.t('cart.percentageRule'),
       },

       quantityCounterKey: 0,
       timer: null,
       isOfferTypeDialogOpen: false,
       bonusTypes:['BONUS FORN','BONUS PROP'],
       currentOfferType:null,
       currentBonus:null,
       amount:0,
       listProductsToCalcByLab:[],
   }),


   mounted() {
       this.toggleInitialOptions();
   },

   watch: {
       hasItems: {
           handler(value, prev) {
               if (!prev) return; // Handle this on mounted()

               if (value && this.summaryPage) {
                   this.toggleActiveOptions(false);
               }
           },
       },

       summaryPage: {
           handler(value) {
               if (value) {
                   this.getItems.forEach((item) => {
                       if (this.hasProductsToShow(item)) {
                           let visible = !this.shouldDisplay(item.id, item.campaign_id) ? true : false;
                           this.setExpandedProduct(item.id, item.campaign_id, visible);
                       }
                   });
               }
           },
           immediate: true,
       },

       'normalOrderStore.getIsLoadingSharedCart': {
           handler: function (IsLoadingSharedCart) {
               this.isLoading = IsLoadingSharedCart;
           },
           immediate: true,
       },
       isDetailedCampaignDialogOpen: {
           handler: function (value) {
               if (!value) {
                   useNormalOrderStore().clearAllOldCarts(this.userStore.user.id, this.branchStore.branch.id, ['baseCampaigns'],true);
               }
           }
       }
   },

   computed: {
       ...mapStores(useBranchStore, useNormalOrderStore, useUserStore,useAppStore),

       parentPage() {
           return this.summaryPage ? 'summary' : 'shoppingCart';
       },

       getItems() {
           const requirements = this.normalOrderStore.getCampaigns
               .map((it) => it.requirements)
               .flat()
               .map((it) => it.product)
               .filter((it) => it.quantity > 0);

           // When there are two requirements for the campaign, we'll only want to parse the campaign once.
           const seenCampaigns = [];
           const seenProducts = [];

           return this.normalOrderStore.getProducts.concat(requirements).flatMap((product) => {
               const campaign = this.normalOrderStore.getCampaignByUniqueIdAndOriginatingProduct(product.campaign_unique_id, product.originating_product_id);
               if (!product.campaign_unique_id || !campaign) {
                   if (seenProducts.includes(`${product.id}`)) {
                       // We're already displaying this product.
                       return [];
                   } else {
                       seenProducts.push(`${product.id}`);
                       return [product];
                   }
               }
               if (seenCampaigns.includes(`${campaign.unique_id}`)) {
                   // We're already displaying this campaign.
                   return [];
               }

               seenCampaigns.push(`${campaign.unique_id}`);

               // @Note: We're only going to display products if they have >0 quantity.
               const requirementsToDisplay = campaign.requirements.filter((r) => r.product.quantity > 0);

               const display = [];

               // Case 1) There's only one requirement => we'll display the campaign with the offers in an indented line.
               // Case 2) There's more than one requirement => we'll display the offers in separate lines.
               const isSeparate = requirementsToDisplay.length !== 1;

               for (const requirement of requirementsToDisplay) {
                   requirement.product.isSeparate = isSeparate;

                   // Special condition for requirement with exceeding quantity
                   if (requirement.product.exceeding_quantity) {
                       const discount = requirement.product.discount;
                       const quantityWithoutDiscount = requirement.product.min_quantity;
                       let quantityWithDiscount = requirement.product.quantity - requirement.product.min_quantity;

                       // Set the total quantity that will be used when the user increments or decrements the product with exceeding quantity
                       requirement.product.totalQuantity = quantityWithoutDiscount + quantityWithDiscount;

                       if (requirement.product.max_quantity && requirement.product.quantity > requirement.product.max_quantity) {
                           quantityWithDiscount = quantityWithDiscount - (requirement.product.quantity - requirement.product.max_quantity);
                       }

                       // Push the product without discount
                       const productWithoutDiscount = { ...requirement.product };
                       productWithoutDiscount.quantity = quantityWithoutDiscount;
                       productWithoutDiscount.discount = 0;
                       productWithoutDiscount.value = 0;
                       display.push(productWithoutDiscount);

                       // Push the product with discount
                       const productWithDiscount = { ...requirement.product };
                       productWithDiscount.quantity = quantityWithDiscount;
                       productWithDiscount.discount = discount;
                       productWithDiscount.isOffer = true;
                       productWithDiscount.uniqueId = productWithDiscount.uniqueId.replace('header', 'offer');
                       display.push(productWithDiscount);
                   } else {
                       display.push(requirement.product);
                   }
               }

               for (const offer of campaign.offers) {
                   offer.product.isSeparate = isSeparate;

                   if (offer.selectedOption && offer.option !== offer.selectedOption) {
                       continue;
                   }

                   if (isSeparate || this.shouldDisplay(product.id, campaign.id)) {
                       display.push(offer.product);
                   }
               }
               return display;
           });
       },

       getAmountOfItems() {
           return this.normalOrderStore.getProductCount;
       },

       showSubstituteProductsColumn() {
           return this.getItems.some((item) => this.hasSubstituteProducts(item));
       },

       getBranchAddresses() {
           let list=this.branchStore.getDeliveryAddresses;
           if(_.size(list)==1){
                   setTimeout(()=>{
                       this.selectedAddress=this.branchStore.getDeliveryAddresses[0]
                   },300)
           }
           return list;
       },

       shoppingCartSubtotal() {
           return this.normalOrderStore.getCartSubtotal;
       },

       shoppingCartIVA() {
           return this.normalOrderStore.getCartVAT;
       },

       shoppingCartTotal() {
           return this.normalOrderStore.getCartTotal;
       },

       ckBudgetRequest: {
           get() {
               return this.normalOrderStore.isBudgetRequest;
           },
           set(value) {
               this.normalOrderStore.setIsBudgetRequest({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       ckScheduleOrder: {
           get() {
               return this.normalOrderStore.isScheduledOrder;
           },
           set(value) {
               this.normalOrderStore.setIsScheduledOrder({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       scheduleOrderDate: {
           get() {
               return this.normalOrderStore.getScheduledOrderDate;
           },
           set(value) {
               this.normalOrderStore.setScheduledOrderDate({ newDate: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       ckFirstOrder: {
           get() {
               return this.normalOrderStore.isFirstOrder;
           },
           set(value) {
               this.normalOrderStore.setIsFirstOrder({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       ckLaboratoryOrder: {
           get() {
               return this.normalOrderStore.isLaboratoryOrder;
           },
           set(value) {
               this.normalOrderStore.setIsLaboratoryOrder({ isRequest: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       firstOrderDiscount: {
           get() {
               return this.normalOrderStore.getFirstOrderDiscount;
           },
       },

       additionalInfoSelected: {
           get() {
               return this.normalOrderStore.selectedAdditionalInfo;
           },
           set(value) {
               this.normalOrderStore.setSelectedAdditionalInfo({ options: value, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       selectedAddress: {
           get() {
               return this.normalOrderStore.getDeliveryAddress?.address ?? null;
           },
           set(address) {
               this.normalOrderStore.setDeliveryAddress({ deliveryAddress: address, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       observation: {
           get() {
               return this.normalOrderStore.getObservation;
           },
           set(observation) {
               this.normalOrderStore.setObservation({ observation: observation, sendSharedCart: this.normalOrderStore.getEnableSharedCartTriggers });
           },
       },

       canViewBonusHeader() {
           return !this.branchNoCampaigns && (this.userStore.hasPermissionTo('View campaigns') || this.canChangeBonusQuantity);
       },

       canChangeBonusQuantity() {
           return this.userStore.hasPermissionTo('Change bonus quantity (as propec)') || this.userStore.hasPermissionTo('Change bonus quantity (as supplier)');
       },

       branchNoCampaigns() {
           return this.branchStore.branch.no_campaigns;
       },
       isMobile() {
           return this.appStore.mobile_ls?false:this.appStore.is_mobile;;
       },
   },

   methods: {
       openTotalByLabs() {
           const products = this.normalOrderStore.getProducts
           const requirements = this.normalOrderStore.getCampaigns
               .map((it) => it.requirements)
               .flat()
               .map((it) => it.product)
               .filter((it) => it.quantity > 0);
           let list  =  _.union(products,requirements)
           let listByLabs= []

           _.each(list,(item)=>{
                let lab = {
                    laboratory:null,
                    subtotal:null,
                    tax:null,
                    total:null
               }
               let hasLab = _.find(listByLabs,{laboratory:item.laboratory.name})
               if(hasLab) {
                   hasLab.subtotal = hasLab.subtotal + (item.price * item.quantity)
                   hasLab.tax =  hasLab.tax + this.formatDecimal(item.quantity * item.price, 2) * (item.vat.tax / 100)
                   hasLab.total =  hasLab.total + this.formatDecimal(item.quantity * item.price, 2) +  this.formatDecimal(item.quantity * item.price, 2) * (item.vat.tax / 100)
               } else {
                   lab.laboratory = item.laboratory.name
                   lab.subtotal = item.price * item.quantity
                   lab.tax = this.formatDecimal(item.quantity * item.price, 2) * (item.vat.tax / 100)
                   lab.total = this.formatDecimal(item.quantity * item.price, 2) +  this.formatDecimal(item.quantity * item.price, 2) * (item.vat.tax / 100)
                   listByLabs.push(lab)
               }
           })
           this.normalOrderStore.setListTotalByLabs(listByLabs)
           this.isTotalByLabsDialogOpen = true
       },
       getLineColor(item){

           if(this.userStore.hasPermissionTo('Change bonus quantity (as propec)')){

               if(_.has(item,'offer_type')){
                   if(item.offer_type==='BONUS PROP')
                       return item.campaign_color? item.campaign_color+' px-0 bonus-prop':' px-0 bonus-prop';
                   if(item.offer_type==='BONUS FORN')
                       return item.campaign_color? item.campaign_color+' px-0 bonus-forn':' px-0 bonus-forn';
               }
           }

           if (!item.campaign_id) return ' px-0' ;

           return item.campaign_color +' px-0' ;
       },
       onClickProductCampaign(product) {
           this.isLoading = true;

           // Close the dialog if it's currently open with another campaign
           if (this.isProductDetailsDialogOpen) this.isProductDetailsDialogOpen = false;
           if (this.isDetailedCampaignDialogOpen) this.isDetailedCampaignDialogOpen = false;

           const targetData = {
               branch_id: this.branchStore.branch?.id,
               product_code: product.code,
           };

           // Obtain fresh data for the product that was used for the latest campaigns dialog trigger
           axios.post('/api/products/single/', targetData).then(({ data }) => {
               this.setSelectedProduct(data[0]);
               this.getCampaigns();
           });
       },

       getCampaigns() {
           this.isLoading = true;

           axios
               .get('/api/campaigns', {
                   params: {
                       reference: this.selectedProduct.code,
                       branch_id: this.branchStore.branch?.id,
                       campaign_id: this.selectedProduct.campaign_id,
                   },
               })
               .then((response) => {
                   this.isLoading = false;

                   let results=response.data
                   _.each(results,(elem)=>{
                       if(elem.description==null){
                           if(elem.offer_quantity>0){
                               let target = String(this.selectedProduct.code);
                               let requirement = _.find(elem.requirements, r => String(r.referenceable) === target);
                               //let requirement = _.find(elem.requirements,{referenceable:''+this.selectedProduct.code})
                               let baseQty = requirement?requirement.min_quantity:elem.requirements[0].min_quantity;
                               let offerQty = elem.offer_quantity
                               elem.description = 'NA COMPRA DE '+baseQty+' UNI, '+offerQty+' DE OFERTA'
                           }
                       }
                   })
                   this.productCampaigns =results;
                   this.originalProductCampaigns = JSON.parse(JSON.stringify(this.productCampaigns));

                   if (this.productCampaigns.length == 0) {
                       this.showError(this.$t('orders.base.campaignsUnavailable'));
                   } else {
                       this.checkAppliedCampaigns();
                       this.isDetailedCampaignDialogOpen = true;
                   }
               })
               .catch((error) => {
                   console.log(error);
                   this.isLoading = false;
               });
       },

       handleStoredMixCampaigns() {
           // Check if there are stored mixs to show
           // We don't want to get the simple campaigns where the originating product id is 999999
           let campaigns = [];
           const all_stored_campaigns = this.normalOrderStore.getCampaignsWithoutSimpleCampaigns;

           if (all_stored_campaigns.length > 0) {
               this.productCampaigns.forEach((campaign) => {
                   const stored_campaigns = all_stored_campaigns.filter((sc) => {
                       return sc.id === campaign.id;
                   });

                   if (stored_campaigns.length > 0) {
                       stored_campaigns.forEach((stored_campaign) => {

                          if(_.size(campaign.requirements) > _.size(stored_campaign.requirements)){
                                let clone = _.cloneDeep(campaign)
                                _.each(clone.requirements,(req)=>{
                                   if(_.findIndex(stored_campaign.requirements,{referenceable:req.referenceable})===-1 && !campaign.split_promotion){
                                       req.campaign_unique_id = stored_campaign.unique_id
                                       req.originating_product.campaign_unique_id = stored_campaign.unique_id
                                       req.product.campaign_unique_id = stored_campaign.unique_id
                                       stored_campaign.requirements.push(req)
                                   }
                                })
                          }
                          campaigns.push(stored_campaign);
                       });
                   } else {
                       campaigns.push(campaign);
                   }
               });
           }
           this.manipulatedProductCampaigns =
               campaigns.length > 0 //
                   ? campaigns.sort((a, b) => a.sort - b.sort || a.index - b.index)
                   : this.productCampaigns.sort((a, b) => a.sort - b.sort || a.index - b.index);
       },

       checkAppliedCampaigns() {
           let campaigns = [];

           this.handleStoredMixCampaigns();
           this.normalOrderStore.restoreBaseCampaigns(this.productCampaigns);

           this.manipulatedProductCampaigns.forEach((campaign) => {
               let finalCampaign;

               let storedCampaign = this.normalOrderStore.getCampaignByUniqueIdAndProduct(campaign.unique_id, this.selectedProduct.id);
               if (storedCampaign) {
                   finalCampaign = storedCampaign;
               } else {
                   let storedCampaignFromAnotherRequirement = this.normalOrderStore.getCampaignByUniqueId(campaign.unique_id);

                   if (storedCampaignFromAnotherRequirement && storedCampaignFromAnotherRequirement.unique_id == campaign.unique_id) {

                       if(campaign.split_promotion && campaign.is_purchased_product_offer && campaign.amount_of_requirements_needed==1){

                           let hasCampaignStored = _.find(this.manipulatedProductCampaigns,{unique_id:campaign.unique_id})
                           let indexCampaign = _.findLastIndex(this.manipulatedProductCampaigns,{id:campaign.id})
                           let lastIndexCampaign = 0
                           let nextIndexCampaign = 0

                           if(indexCampaign == -1){
                               lastIndexCampaign = 0
                           }else{
                               let lastUniqueId = this.manipulatedProductCampaigns[indexCampaign].unique_id
                               let splitLastId = _.split(lastUniqueId,'_')
                               if(_.size(splitLastId)){
                                   lastIndexCampaign = parseInt(splitLastId[1])
                               }
                               nextIndexCampaign = lastIndexCampaign + 1
                           }

                           let groupCampaigns = _.filter(this.manipulatedProductCampaigns,{id:campaign.id})
                           let setNewCamp = false

                           if(hasCampaignStored){
                               if(hasCampaignStored.unique_id == campaign.id + '_' + lastIndexCampaign){
                                   setNewCamp  = !_.some(groupCampaigns, group =>
                                       _.some(group.requirements, r => r.referenceable == '' + this.selectedProduct.code)
                                   );
                               }
                           }

                           if(setNewCamp){

                               let newStoredCampaign = _.cloneDeep(storedCampaignFromAnotherRequirement)
                               newStoredCampaign.unique_id = campaign.id + "_" + nextIndexCampaign
                               if(campaign.is_purchased_product_offer){
                                   newStoredCampaign.offers = []
                               }
                               newStoredCampaign.in_shopping_cart = false
                               let getCampaignReq = _.find(this.productCampaigns,{id:campaign.id})
                               newStoredCampaign.requirements = getCampaignReq.requirements

                               _.each(newStoredCampaign.requirements, (req) => {
                                   req.campaign_unique_id = campaign.id + "_" + nextIndexCampaign
                                   req.product.campaign_unique_id = campaign.id + "_" + nextIndexCampaign
                               })
                               finalCampaign = newStoredCampaign
                           }

                       }else{
                           if(!campaign.split_promotion)
                             finalCampaign = storedCampaignFromAnotherRequirement;
                       }

                   } else {
                       finalCampaign = JSON.parse(JSON.stringify(this.originalProductCampaigns.find((c) => c.id === campaign.id)));
                   }
               }

               if (!finalCampaign || typeof finalCampaign !== 'object') return;

               if (!Array.isArray(finalCampaign.requirements)) {
                   finalCampaign.requirements = [];
               }

               // Add requirements with no quantity from baseCampaigns
               const baseCampaign = this.normalOrderStore.getBaseCampaignByUniqueId?.(campaign.unique_id);
               if (baseCampaign) {
                   if(!campaign.split_promotion){
                       const currentRequirements = Array.isArray(finalCampaign.requirements) ? finalCampaign.requirements : [];
                       const existingRequirementIds = currentRequirements.map((r) => r?.product?.id ?? null).filter((id) => id !== null);

                       const missingRequirements = baseCampaign.requirements.filter((req) => {
                           return !existingRequirementIds.includes(req.product.id);
                       });

                       finalCampaign.requirements.push(...missingRequirements);
                       //finalCampaign.requirements.sort((a, b) => a.product.id - b.product.id);
                   }
               }

               campaigns.push(finalCampaign);
           });

           this.manipulatedProductCampaigns = campaigns.sort((a, b) => a.sort - b.sort || a.index - b.index);
       },


       onCloseDetailedCampaignDialog() {
           this.isDetailedCampaignDialogOpen = false;
       },

       getExpandedId(productId, campaignId) {
           return `${productId}_${campaignId}`;
       },

       shouldDisplay(productId, campaignId) {
           return this.expandedProducts.includes(this.getExpandedId(productId, campaignId));
       },

       setExpandedProduct(productId, campaignId, visible) {
           const expandedId = this.getExpandedId(productId, campaignId);

           if (visible) {
               this.expandedProducts.push(expandedId);
           } else {
               const index = this.expandedProducts.findIndex((id) => id === 999999 || id === expandedId);
               this.expandedProducts.splice(index, 1);
           }
       },

       hasProductsToShow(product) {
           // Not a campaign header -> no products under it.
           if (!product.isHeader || product.isSeparate) return false;

           // A campaign may not have offers, in this case, we don't want to show any lines under it.
           const campaign = this.normalOrderStore.getCampaignByIdAndProduct(product.campaign_id, product.id);
           console.assert(campaign !== undefined, 'The campaign must exist.');

           return campaign.offers.length > 0;
       },

       tryUpdateCampaignQuantity(product, amount) {
           const campaign = this.normalOrderStore.getCampaignByIdAndOriginatingProduct(product.campaign_id, product.originating_product_id);
           const requirement = campaign.requirements.find((it) => it.product.index === product.index);

           // Debounce the search - it will prevent errors when updating too quickly.
           clearTimeout(this.timer);

           this.timer = setTimeout(() => {
               const quantityDiff = Math.abs(requirement.product.quantity - amount);
               const currentRequirementsQuantity =
                   amount > requirement.product.quantity //
                       ? campaign.requirements.reduce((acc, r) => acc + r.product.quantity, 0) + quantityDiff
                       : campaign.requirements.reduce((acc, r) => acc + r.product.quantity, 0) - quantityDiff;

               // If the user has inputted a value in an active campaign product, that makes the campaign invalid
               if (campaign.minimum_value_necessary_quantity > 0 && currentRequirementsQuantity < campaign.minimum_value_necessary_quantity) {
                   this.quantityCounterKey += 1;
                   this.tryRemoveCampaign(product);
                   return;
               }

               this.onCampaignQuantityUpdated(requirement, amount);
           }, 700);
       },

       onCampaignQuantityUpdated(requirement, amount) {
           requirement.product.quantity = this.parseQuantity(amount, true);
           this.changeCampaignQuantity(requirement);
       },

       changeCampaignQuantity(requirement, isIncrement = null) {
           this.setSelectedProduct(requirement.product);

           // At this point, the campaign is always active
           this.normalOrderStore.changeCampaignQuantityOnExisting(requirement.campaign_unique_id, requirement.product.index, isIncrement);
       },

       updateCampaignQuantity(product, isIncrement) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           let quantity = product.quantity;

           if (product.exceeding_quantity) {
               if (product.totalQuantity > product.min_quantity) {
                   isIncrement //
                       ? product.totalQuantity + product.quantity
                       : product.totalQuantity - product.quantity;
               }

               quantity = product.totalQuantity;
           }

           let campaign = this.normalOrderStore.getCampaignByIdAndOriginatingProduct(product.campaign_id, product.originating_product_id);

           if(campaign.split_promotion){
               campaign = this.normalOrderStore.getCampaignByUniqueIdAndRequirementProduct(product.campaign_unique_id,product.id);
           }

           const minimumValueMinQuantity = campaign.requirements.reduce((acc, r) => acc + r.product.quantity, 0) - 1;
           const currentAmountOfRequirementsNeeded = campaign.requirements.filter(function (requirement) {
               return requirement.product.id == product.id ? requirement.product.quantity - product.min_quantity > 0 : requirement.product.quantity > 0;
           }).length;

           const requirement = campaign.requirements.find((requirement) => {
               return requirement.product.isHeader && requirement.product.campaign_id == product.campaign_id && requirement.product.id == product.id;
           });

           !isIncrement &&
               (currentAmountOfRequirementsNeeded < campaign.amount_of_requirements_needed || quantity <= product.min_quantity || minimumValueMinQuantity < campaign.minimum_value_necessary_quantity || (requirement.exceeding_quantity && minimumValueMinQuantity <= requirement.min_quantity))
               ? this.tryRemoveCampaign(product, isIncrement)
               : this.normalOrderStore.changeCampaignQuantityOnExisting(product.campaign_unique_id, product.index, isIncrement, null);
       },

       openProductDetails(product) {
           this.setSelectedProduct(product);
           this.isProductDetailsDialogOpen = true;
       },

       toggleActiveOptions(pageInit = false) {
           // If on pageInit, disable sending data while doing the initial page setup
           // We also restore this value at the end of this function
           if (pageInit) {
               this.normalOrderStore.setEnableSharedCartTriggers(false);
           }

           //{ label: "Programar Encomenda", key: 1 },
           if (this.additionalInfoSelected?.find((option) => option.key == 1) !== undefined) {
               this.ckScheduleOrder = true;
           } else {
               this.ckScheduleOrder = false;
           }

           //{ label: "Encomenda Laboratório", key: 2 },
           if (this.additionalInfoSelected?.find((option) => option.key == 2) !== undefined) {
               this.ckLaboratoryOrder = true;
           } else {
               this.ckLaboratoryOrder = false;
           }

           //{ label: "Primeira Encomenda", key: 3 }
           if (this.additionalInfoSelected?.find((option) => option.key == 3) !== undefined) {
               this.ckFirstOrder = true;
           } else {
               this.ckFirstOrder = false;
           }

           //{ label: "Pedido de Orçamento", key: 0 }
           // Checked last to show a warning correctly when choosing another field while this is already selected
           if (this.additionalInfoSelected?.find((option) => option.key == 0) !== undefined && this.additionalInfoSelected?.find((option) => option.key == 0) !== -1) {
               this.ckBudgetRequest = true;

               let indexToClear = this.additionalInfoSelected?.findIndex((option) => option.key == 1);
               if (indexToClear != -1) {
                   this.additionalInfoSelected.splice(indexToClear, 1);
               }
               indexToClear = null;

               indexToClear = this.additionalInfoSelected?.findIndex((option) => option.key == 2);
               if (indexToClear != -1) {
                   this.additionalInfoSelected.splice(indexToClear, 1);
               }
               indexToClear = null;

               indexToClear = this.additionalInfoSelected?.findIndex((option) => option.key == 3);
               if (indexToClear != -1) {
                   this.additionalInfoSelected.splice(indexToClear, 1);
               }
           } else {
               this.ckBudgetRequest = false;
           }
           if (pageInit) {
               this.normalOrderStore.setEnableSharedCartTriggers(true);
           }
       },

       remove(item) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           if ('campaign_id' in item) {
               this.normalOrderStore.removeCampaign(item.campaign_unique_id, item.id);
               this.isRemoveCampaignDialogOpen = false;
           } else {
               this.normalOrderStore.removeProduct(item.id);
               this.isConfirmDialogOpen = false;
           }
       },

       confirmItemRemoval(item) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           this.isConfirmDialogOpen = true;
           this.setSelectedProduct(item);
       },

       confirmCampaignRemoval(item) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           this.isRemoveCampaignDialogOpen = true;
           this.setSelectedProduct(item);
       },

       increment(item, amount) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           if ((item.bonus === 0 && item.quantity === 1 && amount === -1) || (item.quantity === this.normalOrderStore.getMaxQuantity && amount === 1)) {
               return;
           }

           this.normalOrderStore.incrementProductQuantity(item.id, amount);
       },

       onQuantityUpdated(product, amount) {
           product.quantity = this.parseQuantity(amount, !!product.bonus);
           this.normalOrderStore.setProductQuantity(product.id, product.quantity);
       },

       incrementBonus(product, amount) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           // @Note: `amount` can be negative.
           if ((product.quantity == 0 && product.bonus + amount < 1) || (product.quantity > 0 && product.bonus + amount < 0)) {
               return;
           }
           if(this.userStore.hasPermissionTo('Change bonus quantity (as propec)')){
               if(product.bonus == 0 && amount>0){
                   /*product['offer_type']=null
                   this.currentBonus = product
                   this.amount=amount
                   this.isOfferTypeDialogOpen=true
                   return;*/
                   product['offer_type']='BONUS FORN';
               }
               if(product.bonus == 1 && amount<=0){
                   product['offer_type']=null
               }
           }

           this.normalOrderStore.incrementProductBonus(product.id, amount);
       },
       setBonusTypes(value){
           this.isOfferTypeDialogOpen=false
           this.currentBonus.offer_type=value
           this.$nextTick(_=>{
               this.currentOfferType=null
           })
           this.normalOrderStore.incrementProductBonus(this.currentBonus.id, this.amount);
       },

       onBonusQuantityUpdated(product, amount) {
           product.bonus = this.parseQuantity(amount, product.quantity > 0);
           this.normalOrderStore.setProductBonus(product.id, product.bonus);
       },

       authorizeReplacementDialogOpened(item, index) {
           this.activeIndex = index;
           this.setSelectedProduct(item);
           this.isAuthorizeReplacementDialogOpen = true;
       },

       getProductSubTotalPrice(item) {
           const withDiscount = this.normalOrderStore.calculatePriceWithDiscount(item);
           const withoutDiscount = item.price;

           return this.normalOrderStore.calculateWithExceedingQuantity(item, withDiscount, withoutDiscount);
       },


       calculateHeaderColWidth(field) {
           switch (field) {
           case 'designation':
               if (!this.userStore.hasPermissionTo('View prices')) {
                   return this.calculateColWidth(field);
               }

               if (this.insideDialog) return 'width: 37%; max-width: 37%';
               if (this.summaryPage) return 'width: 43%; max-width: 43%';
               return 'width: 39%; max-width: 39%';
           default:
               return this.calculateColWidth(field);
           }
       },

       calculateColWidth(field) {
           switch (field) {
           case 'designation':
               if (!this.userStore.hasPermissionTo('View prices')) {
                   if (this.insideDialog) return 'width: 52%; max-width: 52%';
                   if (this.summaryPage) return 'width: 55%; max-width: 55%';

                   return 'width: 52%; max-width: 52%';
               }

               if (this.insideDialog) {
                   return 'width: 38%; max-width: 38%';
               } else if (this.summaryPage) {
                   return 'width: 44%; max-width: 44%';
               }
               return 'width: 40%; max-width: 40%';
           case 'quantity':
               if (this.insideDialog) {
                   return 'width: 15%; max-width: 15%';
               }
               return 'width: 13%; max-width: 13%';
           case 'price':
           case 'tax':
           case 'total':
               if (this.insideDialog) {
                   return 'width: 8%; max-width: 8%';
               }
               return 'width: 7%; max-width: 7%';
           case 'actions':
               if (this.insideDialog) {
                   return 'width: 10%; max-width: 10%';
               } else if (this.summaryPage) {
                   return 'width: 0%; max-width: 0%';
               }
               return 'width: 6%; max-width: 6%';
           default:
               return '';
           }
       },

       onCloseAuthorizeProductReplacementDialog() {
           this.isAuthorizeReplacementDialogOpen = false;
       },

       onChangeAuthorizeReplacement(checked, item) {
           if (checked) {
               this.setSelectedProduct(item);
           } else {
               this.selectedProduct = null;
               this.normalOrderStore.setProductSubstitute(item.id, null);
           }
       },

       hasSubstituteProducts(product) {
           const isCampaignProduct = product.campaign_id;
           const hasSubstituteProducts = product.substitute_products && product.substitute_products.length;

           return !isCampaignProduct && hasSubstituteProducts;
       },

       setSelectedProduct(product) {
           this.selectedProduct = product;
       },

       tryRemoveCampaign(product, isIncrement = null) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           this.setSelectedProduct(product);
           const campaign = this.normalOrderStore.getCampaignForOriginatingProduct(product.originating_product_id);

           const minimumValueMinQuantity = campaign.requirements.reduce((acc, r) => {
               if (r.product.id !== product.id) {
                   return acc + r.product.quantity;
               }

               return acc;
           }, 0);

           const fulfilledRequirements = [];

           for (const requirement of campaign.requirements) {
               if (!requirement.product.quantity) continue; // didn't buy in to this requirement.
               fulfilledRequirements.push(requirement);
           }

           if (campaign.amount_of_requirements_needed > 0 && fulfilledRequirements.length > campaign.amount_of_requirements_needed && minimumValueMinQuantity >= campaign.minimum_value_necessary_quantity) {
               if (isIncrement === null) product.quantity = 0;
               this.normalOrderStore.changeCampaignQuantityOnExisting(product.campaign_unique_id, product.index, isIncrement, null);
           } else {
               this.isRemoveCampaignDialogOpen = true;
           }
       },

       maxQuantityCampaign(max_quantity) {
           return max_quantity > 0 ? max_quantity : 9999999;
       },

       isCampaignCollapsed(product) {
           return this.hasProductsToShow(product) ? !this.shouldDisplay(product.id, product.campaign_id) : true;
       },

       offerBonusQuantity(product) {
           if(!this.checkConnection()){
               this.showError(this.$t('common.noInternet'));
               return
           }
           if (product.isOffer) return product.quantity;

           return product.campaign_id != null //
               ? this.isCampaignCollapsed(product)
                   ? this.normalOrderStore.aggregateCampaignRequirementBonus(product)
                   : 0
               : product.bonus;
       },

       isBonusDiscount(product) {
           return product.discount && product.discount > 0 && product.discount != 100;
       },

       campaignIconStyle(product) {
           return this.isCampaignCollapsed(product) ? 'appbar-icon-button-small btn-bg-color-dark-yellow white--text mr-2' : 'mr-2';
       },

       hideQuantityCounter(product) {
           if (product.exceeding_quantity && product.isOffer) return true;
           if (!product.campaign_id || product.isOffer) return false;
           if (!this.hasProductsToShow(product)) return true;

           return !this.isCampaignCollapsed(product);
       },

       createNewOriginalCampaign(campaign) {
           let campaigns = [];

           const newOriginalCampaign = () => {
               const originalCampaign = this.originalProductCampaigns.find((c) => {
                   return c.id == campaign.id;
               });

               if (!originalCampaign) return false;

               const maxIndexCampaign = this.manipulatedProductCampaigns.filter((c) => c.id === campaign.id).reduce((prev, current) => (prev && prev.index > current.index ? prev : current));

               let oc = originalCampaign;
               oc.index = maxIndexCampaign.index + 1;
               oc.unique_id = oc.id + '_' + oc.index;

               // Manipulate requirements unique_id
               oc.requirements.forEach((requirement, index) => {
                   requirement.campaign_unique_id = oc.unique_id;
                   requirement.product.campaign_unique_id = oc.unique_id;
                   requirement.product.uniqueId = requirement.product.id + '_' + oc.unique_id + '_header_' + index;
               });

               // Manipulate requirements unique_id
               oc.offers.forEach((offer, index) => {
                   offer.campaign_unique_id = oc.unique_id;
                   offer.product.campaign_unique_id = oc.unique_id;
                   offer.product.uniqueId = offer.product.id + '_' + oc.unique_id + '_offer_' + index;
               });

               return oc;
           };

           this.manipulatedProductCampaigns.forEach((manipulatedCampaign) => {
               campaigns.push(manipulatedCampaign);

               if (manipulatedCampaign.unique_id === campaign.unique_id) {
                   campaigns.push(JSON.parse(JSON.stringify(newOriginalCampaign())));
               }
           });

           this.manipulatedProductCampaigns = campaigns.sort((a, b) => a.sort - b.sort || a.index - b.index);
       },

       toggleInitialOptions() {
           if (!this.additionalInfoSelected) {
               this.additionalInfoSelected = [];
           }

           this.toggleActiveOptions(true);
       },
   },
};
</script>

<style scoped>
div.v-list-item__content div.v-list-item__title {
   font-size: 0.7rem !important;
   font-weight: 500 !important;
   line-height: 0.8rem !important;
}
.total-by-labs{
   margin-top: -20px;
   margin-right: 10px;
   font-size: 24px!important;
}
</style>
